// Dispatches a selected group's calculation entries to the right renderer -
// ports reporting-hub's GroupPanel.tsx (the "Export Investment Summary"
// affordance from that source is out of scope here - not requested). A group
// carries either:
//  - a single `type: "cash_positions"` entry -> CashPositionsMatrix
//  - a single `type: "nav_calculation"` entry -> NavCalculationMatrix
//  - one-or-more `type: "table"` entries, one per scope (fund + up to 4
//    platforms) -> a Fund/platform switcher + CalculationTable, or
//    VariousStatsPanel for the various_stats/general_overview groups (KPI
//    tile grid instead of a wide table - same special-case as reporting-hub).

import React, { useEffect, useState } from 'react';
import CalculationTable from './CalculationTable';
import VariousStatsPanel from './VariousStatsPanel';
import CashPositionsMatrix from './CashPositionsMatrix';
import NavCalculationMatrix from './NavCalculationMatrix';
import { PLATFORM_ORDER, titleCasePlatform } from './calcFormat';

const SwitcherButton = ({ label, isSelected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`cr-segmented-item${isSelected ? ' is-selected' : ''}`}
    aria-pressed={isSelected}
  >
    {label}
  </button>
);

const GroupPanel = ({ groupId, groupLabel, entries }) => {
  const cashPositionsEntry = (entries || []).find((e) => e.type === 'cash_positions');
  const navCalculationEntry = (entries || []).find((e) => e.type === 'nav_calculation');
  const tableEntries = (entries || []).filter((e) => e.type === 'table');
  const fundEntry = tableEntries.find((e) => e.scope === 'fund');
  const platformEntries = tableEntries.filter((e) => e.scope === 'platform');
  const availablePlatforms = PLATFORM_ORDER.filter((p) => platformEntries.some((e) => e.platform === p));

  const [selectedTab, setSelectedTab] = useState(fundEntry ? 'fund' : availablePlatforms[0] || 'fund');

  // Reset the fund/platform selection whenever the selected group changes -
  // otherwise switching from a group that has a "VAURAUS" tab to one that
  // doesn't would silently show stale/no data instead of falling back.
  useEffect(() => {
    setSelectedTab(fundEntry ? 'fund' : availablePlatforms[0] || 'fund');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const activeEntry = selectedTab === 'fund' ? fundEntry : platformEntries.find((e) => e.platform === selectedTab);

  const showSwitcher = !cashPositionsEntry && !navCalculationEntry && (fundEntry || availablePlatforms.length > 0);
  const useStatsPanel = groupId === 'various_stats' || groupId === 'general_overview';

  return (
    <div>
      <div className="ex-section-header cr-group-header">
        <h4 className="ex-section-title">{groupLabel}</h4>
        {showSwitcher && (
          <div className="cr-segmented">
            {fundEntry && (
              <SwitcherButton label="Fund" isSelected={selectedTab === 'fund'} onClick={() => setSelectedTab('fund')} />
            )}
            {availablePlatforms.map((p) => (
              <SwitcherButton
                key={p}
                label={titleCasePlatform(p)}
                isSelected={selectedTab === p}
                onClick={() => setSelectedTab(p)}
              />
            ))}
          </div>
        )}
      </div>

      {cashPositionsEntry ? (
        <CashPositionsMatrix entry={cashPositionsEntry} />
      ) : navCalculationEntry ? (
        <NavCalculationMatrix entry={navCalculationEntry} />
      ) : activeEntry ? (
        useStatsPanel ? (
          <VariousStatsPanel entry={activeEntry} />
        ) : (
          <CalculationTable entry={activeEntry} />
        )
      ) : (
        <p className="cr-muted-text">No data for this selection.</p>
      )}
    </div>
  );
};

export default GroupPanel;
